import { OperationVariables } from '@apollo/client/core/types';
import { DocumentNode, useApolloClient } from '@apollo/client';
import { FetchPolicy } from '@apollo/client/core/watchQueryOptions';
import { useCallback, useState } from 'react';

/**
 * Query hook for fetching large datasets (>200 rows) when necessary.
 * TO BE USED SPARINGLY - Fetching this much data takes time and puts significant load on the server, we should seek better paging implementations where possible.
 * @param query - Query node
 * @param defaultVariables - Variables to pass to the query by default (Unless overridden when the 'fetch' is called).
 * @param onCompleted - Callback to occur when the processing is complete.
 * @param fetchPolicy - Pass-thru to the apollo query call.
 */
export const useAllPagesQuery = <Result, Variables extends OperationVariables>(
  query: DocumentNode,
  {
    variables: defaultVariables,
    onCompleted,
    fetchPolicy,
  }: {
    variables?: Variables;
    onCompleted?: (data: Result) => void;
    fetchPolicy?: FetchPolicy;
  } = {}
): [
  fetch: (variables?: any) => Promise<Result | undefined>,
  options: { data: Result | undefined; loading: boolean }
] => {
  const client = useApolloClient();
  const [data, setData] = useState<Result | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  //Fields for progress display.
  const [currentPageNumber, setCurrentPageNumber] = useState(0.0);
  const [totalPageCount, setTotalPageCount] = useState(0);

  const fetchAll = useCallback(
    async (overrideVariables?: any) => {
      setLoading(true);
      setData(undefined); //Avoid showing old/outdated information when variables have changed - May take a while to fetch the new data.
      let hasNextPage = true;
      let cursor: number | undefined;
      let lastData: any;
      let currentPage = 0;

      while (hasNextPage) {
        const variables = overrideVariables ?? defaultVariables;

        //Deliberate await in loop so we only fetch one-page-at-a-time
        // eslint-disable-next-line no-await-in-loop
        const { data } = await client.query<Result, Variables>({
          query,
          variables: { ...variables, after: cursor },
          fetchPolicy,
        });
        if (data) {
          lastData = data;
        }

        const pageInfo = (data as any)?.listData?.pageInfo ?? { hasNextPage: false };
        hasNextPage = pageInfo.hasNextPage;
        cursor = pageInfo.endCursor;
        setCurrentPageNumber(++currentPage);
        setTotalPageCount(((data as any)?.listData?.count ?? 0) / (variables.first ?? 200));
      }
      setLoading(false);
      if (lastData) {
        setData(lastData);
        onCompleted?.(lastData);

        return lastData;
      }

      return undefined;
    },
    [client, defaultVariables, fetchPolicy, onCompleted, query]
  );

  let progress;
  if (loading) {
    progress = totalPageCount > 0 ? currentPageNumber / totalPageCount : 0;
  } else {
    progress = 1;
  }

  return [fetchAll, { data, loading, progress }];
};
